/**
 * NewMessagePill
 *
 * A floating pill-shaped button that appears above the sticky input bar in
 * MathCoachPage when new messages arrive while the parent is reading above
 * the viewport bottom. Tapping it triggers a smooth-scroll to the latest
 * message and dismisses the pill.
 *
 * Positioning:
 *   fixed bottom-[72px] — clears the input bar (~56 px) with an 8 px gap.
 *   left-1/2 -translate-x-1/2 — horizontally centred on any viewport width.
 *   z-20 — above message content (z-0) and sticky header/input (z-10).
 *
 * Animation:
 *   200 ms CSS keyframe (pillFadeIn): opacity 0→0.95 + translateY 8px→0.
 *   Runs on the GPU compositor thread (transform + opacity) — no layout jank.
 *
 * AC coverage:
 *   AC-002.2  — pill appears, centred, visible above input area
 *   AC-002.3  — tap anywhere on pill calls onDismiss
 *   AC-002.7  — min-h-[44px] ensures 44 px touch target; bottom-[72px] keeps
 *               pill above the virtual keyboard on a 375 px viewport
 */

export interface NewMessagePillProps {
  /** Whether the pill should be rendered. When false, returns null (no DOM node). */
  visible: boolean;
  /** Called when the user taps the pill. Should trigger scrollToBottom. */
  onDismiss: () => void;
}

/**
 * NewMessagePill — floating indicator for new messages below the viewport.
 */
export function NewMessagePill({ visible, onDismiss }: NewMessagePillProps) {
  if (!visible) return null;

  return (
    <>
      <style>{`
        @keyframes pillFadeIn {
          from { opacity: 0; transform: translateX(-50%) translateY(8px); }
          to   { opacity: 0.95; transform: translateX(-50%) translateY(0); }
        }
      `}</style>
      <button
        type="button"
        aria-label="Scroll to new message"
        onClick={onDismiss}
        className="fixed bottom-[72px] left-1/2 z-20
                   bg-[#1E40AF] text-white text-sm font-semibold
                   rounded-full px-5 py-2 min-h-[44px]
                   shadow-lg cursor-pointer"
        style={{
          opacity: 0.95,
          animation: 'pillFadeIn 200ms ease-out forwards',
          transform: 'translateX(-50%)',
        }}
      >
        New message ↓
      </button>
    </>
  );
}
